import { Link, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

interface SidebarItemProps {
  to?: string;
  icon: ReactNode;
  label: string;
  badge?: number;
  badgeVariant?: 'default' | 'warning';
  isCollapsed: boolean;
  onClick?: () => void;
  isActive?: boolean;
}

export function SidebarItem({
  to,
  icon,
  label,
  badge,
  badgeVariant = 'default',
  isCollapsed,
  onClick,
  isActive: forceActive,
}: SidebarItemProps) {
  const location = useLocation();
  const isActive = forceActive ?? (to ? location.pathname === to : false);

  const baseClasses = `
    flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer
    ${isActive
      ? 'bg-purple-600 text-white'
      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
    }
    ${isCollapsed ? 'justify-center' : ''}
  `;

  // Badge color logic: 0 is grey, >0 is light purple (or warning amber)
  const getBadgeClasses = (collapsed: boolean) => {
    const baseSize = collapsed
      ? 'min-w-[16px] h-[16px] px-1 text-[9px]'
      : 'min-w-[20px] h-5 px-1.5 text-[11px]';

    if (isActive) {
      return `${baseSize} rounded-full font-semibold flex items-center justify-center bg-white/20 text-white`;
    }

    if (badge === 0) {
      return `${baseSize} rounded-full font-semibold flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500`;
    }

    if (badgeVariant === 'warning') {
      return `${baseSize} rounded-full font-semibold flex items-center justify-center bg-amber-500 text-white`;
    }

    // Default: light purple for non-zero
    return `${baseSize} rounded-full font-semibold flex items-center justify-center bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300`;
  };

  // Badge for collapsed state - positioned at bottom-right of icon, further to the right
  const collapsedBadge = isCollapsed && badge !== undefined ? (
    <span className={`absolute -bottom-1 -right-2.5 ${getBadgeClasses(true)}`}>
      {badge > 99 ? '99+' : badge}
    </span>
  ) : null;

  const content = (
    <>
      <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${isCollapsed ? 'relative' : ''}`}>
        {icon}
        {collapsedBadge}
      </span>
      {!isCollapsed && (
        <>
          <span className="flex-1 text-sm font-medium truncate">{label}</span>
          {badge !== undefined && (
            <span className={getBadgeClasses(false)}>{badge}</span>
          )}
        </>
      )}
    </>
  );

  // Tooltip for collapsed state
  const tooltip = isCollapsed ? (
    <div className="absolute left-full ml-2 px-3 py-2 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
      <div className="font-medium">{label}</div>
      {badge !== undefined && (
        <div className="text-xs text-gray-400">Count: {badge}</div>
      )}
    </div>
  ) : null;

  if (to) {
    return (
      <Link to={to} className={`${baseClasses} group relative no-underline`} onClick={onClick}>
        {content}
        {tooltip}
      </Link>
    );
  }

  return (
    <button className={`${baseClasses} group relative w-full`} onClick={onClick}>
      {content}
      {tooltip}
    </button>
  );
}
